import { FlashcardCollection, RecallCollection, MapCollection } from "../types";

export const STORAGE_KEY = "scoresmart_user_data";
export const LEGACY_STORAGE_KEY = "flashgenius_user_data";

export interface AppData {
  collections: FlashcardCollection[];
  recallSheets: RecallCollection[];
  mapSessions: MapCollection[];
  lastUpdated: string;
}

// 1. AUTO-SAVE (Runs silently in background)
export const saveToLocal = (
  collections: FlashcardCollection[],
  recallSheets: RecallCollection[],
  mapSessions: MapCollection[] = []
) => {
  const data: AppData = {
    collections,
    recallSheets,
    mapSessions,
    lastUpdated: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

// 2. AUTO-LOAD (Runs when app starts)
export const loadFromLocal = (): AppData | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.error("Failed to load local data", e);
    return null;
  }
};

// 3. EXPORT FILE (Unified Backup including Atlas)
export const exportAllData = async (
  collections: FlashcardCollection[],
  recallSheets: RecallCollection[],
  mapSessions: MapCollection[] = []
) => {
  let atlasData = null;

  // Attempt to fetch Atlas data from its separate IndexedDB
  try {
    atlasData = await new Promise((resolve, reject) => {
      const request = indexedDB.open("AtlasProDB", 1);
      request.onsuccess = (e: any) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains("atlas")) {
          resolve(null);
          return;
        }
        const transaction = db.transaction(["atlas"], "readonly");
        const store = transaction.objectStore("atlas");
        const getReq = store.get("upscAtlasPro_v18_Data");
        getReq.onsuccess = () => resolve(getReq.result);
        getReq.onerror = () => resolve(null);
      };
      request.onerror = () => resolve(null);
    });
  } catch (e) {
    console.warn("Could not fetch Atlas data for export", e);
  }

  const data: AppData & { atlas?: any } = {
    collections,
    recallSheets,
    mapSessions,
    atlas: atlasData,
    lastUpdated: new Date().toISOString(),
  };

  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = `ScoreSmart_FullBackup_${new Date()
    .toISOString()
    .slice(0, 10)}.fgdata`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  alert("Global Backup Created Successfully! This file contains all your Flashcards, Recall Sheets, and Atlas Notebooks.");
};

export const exportDataToFile = (
  collections: FlashcardCollection[],
  recallSheets: RecallCollection[]
) => {
  const data: AppData = {
    collections,
    recallSheets,
    mapSessions: [], // Default empty for older export format compatibility
    lastUpdated: new Date().toISOString(),
  };

  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = `ScoreSmart_Backup_${new Date()
    .toISOString()
    .slice(0, 10)}.fgdata`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// 4. IMPORT FILE (User uploads file)
export const importDataFromFile = (file: File): Promise<AppData & { atlas?: any }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const data = JSON.parse(text);

        // Validation: Expecting at least collections or recallSheets
        if (
          !Array.isArray(data.collections) &&
          !Array.isArray(data.recallSheets)
        ) {
          throw new Error("Invalid file format");
        }

        resolve(data);
      } catch (err) {
        reject(new Error("Failed to parse file. Is it a valid .fgdata file?"));
      }
    };
    reader.readAsText(file);
  });
};

/**
 * Programmatically overwrite Atlas Pro's IndexedDB data.
 * This is used for cross-tool global data restoration.
 */
export const saveAtlasToIndexedDB = (atlasData: any): Promise<void> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("AtlasProDB", 1);
    request.onsuccess = (e: any) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains("atlas")) {
        // If store doesn't exist, we can't save (should have been created by Atlas tool)
        // But for fresh imports, we might need to wait or alert.
        console.warn("Atlas object store not found during import.");
        resolve();
        return;
      }
      const transaction = db.transaction(["atlas"], "readwrite");
      const store = transaction.objectStore("atlas");
      store.put(atlasData, "upscAtlasPro_v18_Data");
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(new Error("Failed to save Atlas data to IDB"));
    };
    request.onerror = () => reject(new Error("Failed to open AtlasProDB"));
  });
};
